// Re-authored from ../design-system/components/feedback/Dialog.jsx.
// CONTROLLED overlay — open prop gates render; return null when closed.
//
// NOTE: No focus-trap / initial-focus (matches DS; full focus-trap deferred to R5 a11y polish).
// Renders inline (no portal — matches DS).

import { useEffect, type MouseEvent, type ReactNode } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface DialogProps {
  open: boolean;
  onClose?: () => void;
  title?: ReactNode;
  description?: ReactNode;
  side?: boolean;
  footer?: ReactNode;
  children?: ReactNode;
}

export function Dialog({ open, onClose, title, description, side = false, footer, children }: DialogProps) {
  // Escape key closes the dialog
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose?.();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  function handleOverlayClick(e: MouseEvent<HTMLDivElement>) {
    // Only close when clicking directly on the overlay, not its children
    if (e.target === e.currentTarget) onClose?.();
  }

  return (
    /* Overlay */
    <div
      className={cn(
        'fixed inset-0 z-[1000] flex items-center justify-center p-6',
        'bg-[rgba(15,23,42,0.55)] backdrop-blur-[3px] ollio-overlay-anim',
        side && 'justify-end p-0',
      )}
      onClick={handleOverlayClick}
    >
      {/* Dialog / Sheet panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        className={cn(
          'bg-card shadow-[var(--shadow-xl)] w-full flex flex-col overflow-hidden font-sans',
          !side && 'rounded-[var(--radius-card)] max-w-[460px] max-h-[90vh] ollio-dialog-anim',
          side && 'max-w-[440px] h-screen max-h-screen rounded-none ollio-sheet-anim',
        )}
      >
        {/* Head */}
        <div className="flex items-start justify-between gap-4 p-5 pb-3">
          {/* Title + description column */}
          <div className="flex flex-col gap-1 min-w-0">
            {title != null && (
              <h2 className="font-serif text-lg font-bold text-[var(--text-heading)] m-0 leading-snug">
                {title}
              </h2>
            )}
            {description != null && (
              <p className="text-sm text-muted m-0">{description}</p>
            )}
          </div>
          {/* Close button */}
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className={cn(
              'h-8 w-8 rounded-sm text-muted flex items-center justify-center flex-none',
              'hover:bg-[var(--ollio-navy-light)] hover:text-[var(--ollio-navy)]',
              'transition-colors',
            )}
          >
            <X className="h-[18px] w-[18px]" aria-hidden="true" />
          </button>
        </div>

        {/* Body */}
        {children != null && (
          <div className="px-5 pb-5 pt-2 overflow-y-auto text-foreground">
            {children}
          </div>
        )}

        {/* Footer */}
        {footer != null && (
          <div className="flex justify-end gap-3 p-4 px-5 border-t border-[var(--border-soft)] bg-input">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
